import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { H4 } from 'foundations-components/transitional/components/H4';
import { H5 } from 'foundations-components/transitional/components/H5';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { FileButton } from 'foundations-components/transitional/components/FileButton';
import { Image } from 'foundations-components/transitional/components/Image';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { InsertImageIcon } from 'foundations-assets/icons/components/InsertImageIcon';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { H7 } from 'foundations-components/transitional/components/H7';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { View } from 'foundations-components/transitional/components/View';
import { HR } from 'foundations-components/transitional/components/HR';
import { ChirpError } from 'rpc-client-utils';
import { useLoginVerifyQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/AccessRpc.hooks';
import { useUploadProfilePhotoMutation } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/SettingsRpc.hooks';
import { getPortalAccountId, getPortalInstanceId } from '../utils/metadata';
import { usePortalsPlatformObjectId } from '../context/PortalsPlatformObjectIdContext';
import { Flex } from 'foundations-components/components/Flex';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { useIsUngated } from '../hooks/useIsUngated';
import { PasswordResetPanel } from './PasswordResetPanel';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PANEL_WIDTH_BY_BREAKPOINT = {
  mobile: '100%',
  tablet: '80%',
  desktop: 600
};
const PROFILE_PHOTO_UPLOAD_GATE = 'PortalsPlatform::Settings::ProfilePhotoUpload';
const RESET_PASSWORD_GATE = 'PortalsPlatform::Settings::ResetPassword';
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg'];
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
function useProfilePhotoUpload(loginVerifyData, refetchLoginVerify) {
  var _ref;
  const [pendingFile, setPendingFile] = useState();
  const [localAvatarUrl, setLocalAvatarUrl] = useState();
  const [uploadedAvatarUrl, setUploadedAvatarUrl] = useState();
  const [imageFailed, setImageFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const accountId = getPortalAccountId();
  const portalInstanceId = getPortalInstanceId();
  const objectId = usePortalsPlatformObjectId();
  const [uploadProfilePhoto, {
    loading: uploading
  }] = useUploadProfilePhotoMutation();
  const localAvatarUrlRef = useRef(localAvatarUrl);
  localAvatarUrlRef.current = localAvatarUrl;
  const displayUrl = (_ref = localAvatarUrl !== null && localAvatarUrl !== void 0 ? localAvatarUrl : uploadedAvatarUrl) !== null && _ref !== void 0 ? _ref : loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.avatarUrl;
  useEffect(() => {
    return () => {
      if (localAvatarUrlRef.current) {
        URL.revokeObjectURL(localAvatarUrlRef.current);
      }
    };
  }, []);
  const showImage = Boolean(displayUrl) && !imageFailed;
  const handleImageError = useCallback(() => {
    setImageFailed(true);
    if (localAvatarUrl) {
      URL.revokeObjectURL(localAvatarUrl);
      setLocalAvatarUrl(undefined);
      setErrorMessage(unescapedText('app.myAccountPopover.settings.profilePhoto.errorUploadFailed'));
    }
  }, [localAvatarUrl]);
  const resolveUploadErrorMessage = useCallback(error => {
    var _networkError$cause, _networkError;
    const rpcError = (_networkError$cause = error === null || error === void 0 || (_networkError = error.networkError) === null || _networkError === void 0 ? void 0 : _networkError.cause) !== null && _networkError$cause !== void 0 ? _networkError$cause : error === null || error === void 0 ? void 0 : error.cause;
    const settingsError = rpcError instanceof ChirpError ? rpcError : undefined;
    switch (settingsError === null || settingsError === void 0 ? void 0 : settingsError.reason) {
      case 'FILE_TOO_LARGE':
        return unescapedText('app.myAccountPopover.settings.profilePhoto.errorFileTooLarge');
      case 'UNSUPPORTED_FILE_TYPE':
        return unescapedText('app.myAccountPopover.settings.profilePhoto.errorInvalidType');
      default:
        return unescapedText('app.myAccountPopover.settings.profilePhoto.errorUploadFailed');
    }
  }, []);
  const handleFileChange = useCallback(event => {
    var _event$target$files;
    const file = (_event$target$files = event.target.files) === null || _event$target$files === void 0 ? void 0 : _event$target$files[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setErrorMessage(unescapedText('app.myAccountPopover.settings.profilePhoto.errorInvalidType'));
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrorMessage(unescapedText('app.myAccountPopover.settings.profilePhoto.errorFileTooLarge'));
      return;
    }
    setErrorMessage(undefined);
    setImageFailed(false);
    const previewUrl = URL.createObjectURL(file);
    if (localAvatarUrl) {
      URL.revokeObjectURL(localAvatarUrl);
    }
    setLocalAvatarUrl(previewUrl);
    setPendingFile(file);
  }, [localAvatarUrl]);
  const save = useCallback(async () => {
    if (!pendingFile) {
      return;
    }
    const previewUrl = localAvatarUrl;
    try {
      var _data$result;
      const imageData = await fileToBase64(pendingFile);
      const {
        data
      } = await uploadProfilePhoto({
        variables: {
          imageData,
          contentType: pendingFile.type,
          accountId,
          portalInstanceId,
          objectId
        }
      });
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setLocalAvatarUrl(undefined);
      setPendingFile(undefined);
      const avatarUrl = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 ? void 0 : _data$result.avatarUrl;
      if (avatarUrl) {
        setUploadedAvatarUrl(avatarUrl);
      }
      await (refetchLoginVerify === null || refetchLoginVerify === void 0 ? void 0 : refetchLoginVerify());
    } catch (error) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setLocalAvatarUrl(undefined);
      setPendingFile(undefined);
      setErrorMessage(resolveUploadErrorMessage(error));
    }
  }, [pendingFile, localAvatarUrl, uploadProfilePhoto, accountId, portalInstanceId, objectId, resolveUploadErrorMessage, refetchLoginVerify]);
  return {
    displayUrl,
    showImage,
    uploading,
    errorMessage,
    handleFileChange,
    handleImageError,
    save
  };
}
function ProfilePhotoSection({
  loginVerifyData,
  photo
}) {
  const {
    displayUrl,
    showImage,
    uploading,
    errorMessage,
    handleFileChange
  } = photo;
  const handleImageError = photo.handleImageError;
  const initials = useMemo(() => {
    const firstName = loginVerifyData.firstname || '';
    const lastName = loginVerifyData.lastname || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  }, [loginVerifyData]);
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    gap: "400",
    marginBlock: "300",
    children: [/*#__PURE__*/_jsxs(Flex, {
      position: "relative",
      inlineSize: "64px",
      blockSize: "64px",
      children: [showImage ? /*#__PURE__*/_jsx(Image, {
        src: displayUrl,
        alt: "Profile",
        shape: "circle",
        responsive: false,
        inlineSize: "64px",
        blockSize: "64px",
        objectFit: "cover",
        bordered: true,
        borderColor: "border-core-default",
        onError: handleImageError
      }) : /*#__PURE__*/_jsx(Flex, {
        backgroundColor: "fill-primary-pressed",
        borderRadius: "full",
        borderWidth: "100",
        borderStyle: "solid",
        borderColor: "border-core-default",
        inlineSize: "64px",
        blockSize: "64px",
        alignItems: "center",
        justifyContent: "center",
        children: /*#__PURE__*/_jsx(Paragraph, {
          color: "text-inverse-default",
          marginBlock: "0",
          typography: "heading-200",
          children: initials
        })
      }), uploading && /*#__PURE__*/_jsx(Flex, {
        position: "absolute",
        insetBlockStart: "0",
        insetInlineStart: "0",
        inlineSize: "64px",
        blockSize: "64px",
        borderRadius: "full",
        backgroundColor: "fill-surface-default",
        alignItems: "center",
        justifyContent: "center",
        children: /*#__PURE__*/_jsx(LoadingSpinner, {
          size: "xs"
        })
      }), /*#__PURE__*/_jsx(Flex, {
        position: "absolute",
        insetInlineEnd: "0",
        insetBlockEnd: "0",
        children: /*#__PURE__*/_jsx(FileButton, {
          use: "unstyled",
          paddingInline: "0",
          accept: ['image/png', 'image/jpeg'],
          onChange: handleFileChange,
          disabled: uploading,
          "aria-label": unescapedText('app.myAccountPopover.settings.profilePhoto.uploadButton'),
          children: /*#__PURE__*/_jsx(Flex, {
            inlineSize: "24px",
            blockSize: "24px",
            borderRadius: "full",
            backgroundColor: "fill-primary-default",
            alignItems: "center",
            justifyContent: "center",
            children: /*#__PURE__*/_jsx(InsertImageIcon, {
              size: "100",
              color: "text-primary-default"
            })
          })
        })
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "50",
      alignItems: "start",
      children: [/*#__PURE__*/_jsx(Paragraph, {
        color: "text-core-default",
        marginBlock: "0",
        typography: "body-100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.myAccountPopover.settings.profilePhoto.title"
        })
      }), /*#__PURE__*/_jsx(Paragraph, {
        color: "text-core-default",
        marginBlock: "0",
        typography: "detail-200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.myAccountPopover.settings.profilePhoto.description"
        })
      }), /*#__PURE__*/_jsx(FileButton, {
        use: "secondary-ghost",
        size: "small",
        accept: ['image/png', 'image/jpeg'],
        onChange: handleFileChange,
        disabled: uploading,
        children: /*#__PURE__*/_jsx(Paragraph, {
          color: "text-core-default",
          marginBlock: "0",
          typography: "detail-200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.profilePhoto.uploadButton"
          })
        })
      }), errorMessage && /*#__PURE__*/_jsxs(Paragraph, {
        color: "text-core-default",
        marginBlock: "0",
        typography: "detail-200",
        children: ["*", errorMessage]
      })]
    })]
  });
}
function SettingsForm({
  loginVerifyData,
  loading,
  onOpenChangePassword,
  refetchLoginVerify
}) {
  const isProfilePhotoUploadEnabled = useIsUngated(PROFILE_PHOTO_UPLOAD_GATE);
  const isResetPasswordEnabled = useIsUngated(RESET_PASSWORD_GATE);
  const photo = useProfilePhotoUpload(loginVerifyData, refetchLoginVerify);
  const [saving, setSaving] = useState(false);
  const handleSaveChanges = useCallback(async () => {
    setSaving(true);
    try {
      await photo.save();
    } finally {
      setSaving(false);
    }
  }, [photo]);
  const EMPTY_PLACEHOLDER = '\u2014';
  const fullName = loginVerifyData && [loginVerifyData.firstname, loginVerifyData.lastname].filter(Boolean).join(' ') || EMPTY_PLACEHOLDER;
  const jobTitle = (loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.jobTitle) || EMPTY_PLACEHOLDER;
  const phone = (loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.phone) || EMPTY_PLACEHOLDER;
  const email = (loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.email) || EMPTY_PLACEHOLDER;
  const company = (loginVerifyData === null || loginVerifyData === void 0 ? void 0 : loginVerifyData.company) || EMPTY_PLACEHOLDER;
  return /*#__PURE__*/_jsxs(View, {
    children: [loginVerifyData ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [isProfilePhotoUploadEnabled && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ProfilePhotoSection, {
          loginVerifyData: loginVerifyData,
          photo: photo
        }), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        })]
      }), /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        marginBlockEnd: "300",
        children: [/*#__PURE__*/_jsx(H5, {
          color: "text-core-default",
          marginBlockStart: "300",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.account.title"
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsx(Fieldset, {
            _size: "small",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: unescapedText('app.myAccountPopover.settings.account.membershipEmailLabel'),
              help: unescapedText('app.myAccountPopover.settings.account.membershipEmailTooltip'),
              children: /*#__PURE__*/_jsx(TextInput, {
                name: "membershipEmail",
                value: email,
                typography: "body-100",
                readOnly: true,
                disabled: true
              })
            })
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsx(Fieldset, {
            _size: "small",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: unescapedText('app.myAccountPopover.settings.account.companyLabel'),
              children: /*#__PURE__*/_jsx(TextInput, {
                name: "company",
                value: company,
                typography: "body-100",
                readOnly: true,
                disabled: true
              })
            })
          })
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      }), /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        marginBlockStart: "300",
        marginBlockEnd: "300",
        children: [/*#__PURE__*/_jsx(H5, {
          color: "text-core-default",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.profile.title"
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "200",
          flexGrow: 1,
          minInlineSize: "0",
          children: /*#__PURE__*/_jsx(Fieldset, {
            _size: "small",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: unescapedText('app.myAccountPopover.settings.profile.fullNameLabel'),
              children: /*#__PURE__*/_jsx(TextInput, {
                name: "fullName",
                value: fullName,
                typography: "body-100",
                readOnly: true,
                disabled: true
              })
            })
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "200",
          flexGrow: 1,
          minInlineSize: "0",
          children: /*#__PURE__*/_jsx(Fieldset, {
            _size: "small",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: unescapedText('app.myAccountPopover.settings.profile.jobTitleLabel'),
              children: /*#__PURE__*/_jsx(TextInput, {
                name: "jobTitle",
                value: jobTitle,
                typography: "body-100",
                readOnly: true,
                disabled: true
              })
            })
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(Fieldset, {
            _size: "small",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: unescapedText('app.myAccountPopover.settings.profile.phoneLabel'),
              children: /*#__PURE__*/_jsx(TextInput, {
                name: "phone",
                value: phone,
                typography: "body-100",
                readOnly: true,
                disabled: true
              })
            })
          })
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    }) : loading ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        role: "status",
        "aria-label": unescapedText('app.myAccountPopover.settings.loadingLabel'),
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "400",
          marginBlock: "300",
          children: [/*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "full",
            inlineSize: "64px",
            blockSize: "64px"
          }), /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            gap: "100",
            flexGrow: 1,
            children: [/*#__PURE__*/_jsx(View, {
              backgroundColor: "fill-accent-neutral-subtle",
              borderRadius: "100",
              inlineSize: "140px",
              blockSize: "14px"
            }), /*#__PURE__*/_jsx(View, {
              backgroundColor: "fill-accent-neutral-subtle",
              borderRadius: "100",
              inlineSize: "180px",
              blockSize: "12px"
            })]
          })]
        }), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        }), /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "200",
          marginBlock: "300",
          children: [/*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100px",
            blockSize: "18px"
          }), /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100%",
            blockSize: "32px"
          }), /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100%",
            blockSize: "32px"
          })]
        }), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        }), /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "200",
          marginBlock: "300",
          children: [/*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100px",
            blockSize: "18px"
          }), /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100%",
            blockSize: "32px"
          }), /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100%",
            blockSize: "32px"
          }), /*#__PURE__*/_jsx(View, {
            backgroundColor: "fill-accent-neutral-subtle",
            borderRadius: "100",
            inlineSize: "100%",
            blockSize: "32px"
          })]
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        alignItems: "center",
        gap: "200",
        paddingBlock: "500",
        role: "alert",
        children: [/*#__PURE__*/_jsx(Paragraph, {
          color: "text-core-default",
          marginBlock: "0",
          typography: "heading-200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.error.title"
          })
        }), /*#__PURE__*/_jsx(Paragraph, {
          color: "text-core-default",
          marginBlock: "0",
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.error.description"
          })
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    }), isResetPasswordEnabled && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        marginBlockStart: "300",
        marginBlockEnd: "300",
        children: [/*#__PURE__*/_jsx(H5, {
          color: "text-core-default",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.security.title"
          })
        }), /*#__PURE__*/_jsx(View, {
          children: /*#__PURE__*/_jsx(Fieldset, {
            _size: "small",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: unescapedText('app.myAccountPopover.settings.security.changePasswordLabel'),
              children: /*#__PURE__*/_jsx(Flex, {
                inlineSize: "100%",
                children: /*#__PURE__*/_jsx(Button, {
                  use: "unstyled",
                  onClick: onOpenChangePassword,
                  flex: "auto",
                  children: /*#__PURE__*/_jsx(View, {
                    inlineSize: "100%",
                    borderWidth: "100",
                    borderStyle: "solid",
                    borderColor: "border-core-default",
                    borderRadius: "200",
                    backgroundColor: "fill-surface-default",
                    paddingInline: "200",
                    paddingBlock: "100",
                    children: /*#__PURE__*/_jsxs(Flex, {
                      alignItems: "center",
                      justifyContent: "space-between",
                      inlineSize: "100%",
                      gap: "200",
                      children: [/*#__PURE__*/_jsxs(Flex, {
                        alignItems: "center",
                        gap: "200",
                        children: [/*#__PURE__*/_jsx(LockedIcon, {
                          size: "300",
                          color: "text-core-default"
                        }), /*#__PURE__*/_jsx(Paragraph, {
                          color: "text-core-default",
                          marginBlock: "0",
                          typography: "body-100",
                          children: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: "app.myAccountPopover.settings.security.changePassword"
                          })
                        })]
                      }), /*#__PURE__*/_jsx(RightIcon, {
                        size: "300",
                        color: "text-core-default"
                      })]
                    })
                  })
                })
              })
            })
          })
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    }), isProfilePhotoUploadEnabled && /*#__PURE__*/_jsx(LoadingButton, {
      use: "primary",
      block: true,
      marginBlockStart: "400",
      onClick: () => void handleSaveChanges(),
      loading: saving,
      disabled: saving,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.myAccountPopover.settings.saveChanges"
      })
    })]
  });
}
export function SettingsPanel({
  onClose
}) {
  const breakpoint = useBreakpoint();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const accountId = getPortalAccountId();
  const portalInstanceId = getPortalInstanceId();
  const objectId = usePortalsPlatformObjectId();
  const {
    data: loginVerifyData,
    loading,
    refetch: refetchLoginVerify
  } = useLoginVerifyQuery({
    variables: {
      accountId,
      portalInstanceId,
      objectId
    }
  });
  const isMobile = breakpoint === 'mobile';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Panel, {
      width: PANEL_WIDTH_BY_BREAKPOINT[breakpoint],
      children: isMobile ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Flex, {
          position: "sticky",
          insetBlockStart: "0",
          zIndex: "1",
          backgroundColor: "fill-surface-default",
          borderBlockEndWidth: "100",
          borderBlockEndStyle: "solid",
          borderBlockEndColor: "border-core-default",
          paddingInline: "400",
          paddingBlock: "300",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "200",
          children: [/*#__PURE__*/_jsx(H4, {
            color: "text-core-default",
            marginBlock: "0",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.myAccountPopover.settings.heading"
            })
          }), /*#__PURE__*/_jsx(CloseButton, {
            inline: true,
            onClick: onClose,
            color: "text-core-default"
          })]
        }), /*#__PURE__*/_jsx(PanelBody, {
          children: /*#__PURE__*/_jsxs(PanelSection, {
            children: [/*#__PURE__*/_jsx(H7, {
              color: "text-core-default",
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.subtitle"
              })
            }), /*#__PURE__*/_jsx(HR, {
              distance: "flush"
            }), /*#__PURE__*/_jsx(SettingsForm, {
              loginVerifyData: loginVerifyData,
              loading: loading,
              onOpenChangePassword: () => setShowChangePassword(true),
              refetchLoginVerify: refetchLoginVerify
            })]
          })
        })]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(CloseButton, {
          onClick: onClose,
          color: "text-core-default"
        }), /*#__PURE__*/_jsx(PanelBody, {
          children: /*#__PURE__*/_jsxs(PanelSection, {
            children: [/*#__PURE__*/_jsx(H4, {
              color: "text-core-default",
              marginBlockEnd: "200",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.heading"
              })
            }), /*#__PURE__*/_jsx(H7, {
              color: "text-core-default",
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.subtitle"
              })
            }), /*#__PURE__*/_jsx(HR, {
              distance: "flush"
            }), /*#__PURE__*/_jsx(SettingsForm, {
              loginVerifyData: loginVerifyData,
              loading: loading,
              onOpenChangePassword: () => setShowChangePassword(true),
              refetchLoginVerify: refetchLoginVerify
            })]
          })
        })]
      })
    }), showChangePassword && /*#__PURE__*/_jsx(PasswordResetPanel, {
      onClose: () => setShowChangePassword(false)
    })]
  });
}